import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customersApi } from '@/api/customers';
import {
  DeploymentReason,
  OwnerEntity,
  UserRole,
  contractTypeToLineReason,
  type CustomerSummaryDto,
  type StoreSummaryDto,
} from '@bhops/shared';
import { dealsApi, type CreateDealBody } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { CustomerPicker } from '@/components/pickers/CustomerPicker';
import { StorePicker } from '@/components/pickers/StorePicker';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canSeePrice } from '@/permissions';

// New Deal modal — design §4.1.
//
// Auto-fills `ownerEntity` from the selected customer's
// ownerEntity (server enforces coherence anyway, but the modal
// hides the field so non-admins don't have to think about it).
// Admins can override via the disabled-for-non-admin select if
// they need to create a Deal in a different entity than the
// customer's (rare, but the server allows it).

const SALES_REP_CODES = ['J', 'A', 'K', 'T', 'D', 'BH'] as const;
type SalesRepCode = (typeof SALES_REP_CODES)[number];

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Plan v0.3.0 §F-3 follow-up — when launched from
   * CustomerMasterPage's embedded DealsKanbanPage, pre-select
   * this customer so operators don't re-pick.
   */
  initialCustomerId?: string;
}

interface DraftDeal {
  customer: CustomerSummaryDto | null;
  store: StoreSummaryDto | null;
  ownerEntity: OwnerEntity;
  salesRepCode: SalesRepCode;
  // BigInt over the wire — kept as numeric string in form state.
  expectedRevenue: string;
  expectedRobotCount: string;
  // §D-2 (PDF p.13) — contract-prefill fields entered during sales.
  expectedUnitPrice: string; // BigInt-as-string (KRW per robot)
  expectedContractType: string; // OptionGroup `deal.contractType`
  expectedContractDurationMonths: string; // OptionGroup `deal.contractDuration`
}

function emptyDraft(defaultEntity: OwnerEntity): DraftDeal {
  return {
    customer: null,
    store: null,
    ownerEntity: defaultEntity,
    salesRepCode: 'J',
    expectedRevenue: '',
    expectedRobotCount: '',
    expectedUnitPrice: '',
    expectedContractType: '',
    expectedContractDurationMonths: '',
  };
}

// 계약 사실화 1단계 정정7(26-07-29) — 금액 칸의 안내문을 **저장 결과대로** 적는다.
//
// 종전 안내문("Q#7 — 계약 시 Contract.unitPriceMonthly 로 매핑")은 두 가지가 틀렸다:
//   ① 사실이 아니다. 직전 정정(정정6) 이후 이 금액은 **계약 형태에 따라 갈린다** —
//      판매 딜이면 계약의 **판매가** 칸으로 가고 월 구독료 칸에는 0 이 들어가며,
//      렌탈 딜이면 종전대로 월 구독료 칸으로 간다(apps/api contracts.service
//      createSignedFromDeal). 즉 **금액을 입력하는 사람이 읽는 문장이 저장 결과와
//      반대**였다 — 이번 공사가 없애려는 '화면이 지키지 못할 약속'과 같은 종류다.
//   ② 내부 질문 번호와 컬럼명을 운영자 화면에 노출했다. 같은 모달의 다른 안내문
//      ('대당 단가 × 수량으로 자동 계산되거나 수동 입력 가능')처럼 일상어로 적는다.
//
// ⚠ 금액의 뜻을 여기서 새로 정하지 않았다 — 저장 규칙 정본(shared
// contractTypeToLineReason)을 그대로 불러 **화면이 서버와 같은 판정을 말하게** 한다
// (계약 형태 옵션 값이 늘거나 바뀌어도 문구가 따라온다). 라벨('대당, KRW')과 스키마
// 주석('KRW per robot')은 둘 다 "대당 금액"이라고만 말하므로 이 문구와 모순이 없다 —
// 어긋나 있던 것은 안내문 한 줄뿐이었다.
//
// 단어는 이 리포가 이미 쓰는 것을 쓴다: 월 금액 = '구독료'(고객사 화면 '구독료(대당)'),
// 판매 금액 = '판매가'(같은 화면 '판매가(대당)').
export function unitPriceMeaningHint(expectedContractType: string): string {
  const reason = contractTypeToLineReason(expectedContractType);
  if (reason === DeploymentReason.SALE) {
    return '판매 계약은 대당 판매가로 기록됩니다. 월 구독료는 없습니다.';
  }
  if (reason === DeploymentReason.LEASE_OPERATING) {
    return '렌탈 계약은 대당 월 구독료로 기록됩니다.';
  }
  // 서버는 인식하지 못하는 계약 형태(미선택 포함)를 렌탈로 본다
  // (contractTypeToLineReason(...) ?? LEASE_OPERATING).
  return '계약 형태에 따라 뜻이 갈립니다. 렌탈이면 대당 월 구독료, 판매면 대당 판매가입니다. 안 고르면 렌탈로 기록됩니다.';
}

export function DealCreateModal({ open, onClose, initialCustomerId }: Props) {
  const user = useAuthStore((s) => s.user);
  // §D-3 (PDF p.17 ②) — non-sales roles don't see (or enter) price values.
  // Server-side interceptor strips the fields from the response too, but
  // the input rows are hidden client-side so the wrong-team operator
  // doesn't accidentally type a number into a redacted field.
  const showPriceInputs = !!user && canSeePrice(user.role);
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<DraftDeal>(() =>
    emptyDraft(user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB),
  );
  const [error, setError] = useState<string | null>(null);

  // Plan v0.3.0 §F-3 follow-up — when initialCustomerId is provided
  // (CustomerMasterPage embed context), fetch that customer so the
  // form's `customer` field can be pre-populated. CustomerPicker
  // accepts a CustomerSummaryDto via setForm, not a bare ID — hence
  // the explicit fetch.
  const initialCustomerQ = useQuery({
    queryKey: ['customers', 'detail', initialCustomerId ?? null],
    queryFn: () => customersApi.get(initialCustomerId!),
    enabled: open && !!initialCustomerId,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!open || !initialCustomerQ.data) return;
    setForm((f) => {
      // Don't clobber if the user has already picked a different customer.
      if (f.customer && f.customer.id !== initialCustomerQ.data!.id) return f;
      return {
        ...f,
        customer: initialCustomerQ.data!,
        ownerEntity: initialCustomerQ.data!.ownerEntity,
      };
    });
  }, [open, initialCustomerQ.data]);

  const create = useMutation({
    mutationFn: (body: CreateDealBody) => dealsApi.create(body),
    onSuccess: (deal) => {
      // round4 #5a (BUG#3) — 고객사 상세의 견적 카드(DealQuoteCard)는 customer-master
      // 에서 합성되므로 deals 만으론 stale. cascade 로 일괄 무효화.
      invalidateCustomerMasterCascade(qc);
      // PDF p.13: 운영 용어로 "Deal" → "고객사" 통일.
      toast.success(
        `${deal.humanId} 딜을 만들었습니다. 자동 분류는 ${deal.category}입니다.`,
        '고객사 생성 완료',
      );
      onClose();
      setForm(emptyDraft(user?.ownerEntity ?? OwnerEntity.BEYOND_HONEYCOMB));
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);

    if (!form.customer) {
      setError('고객사를 선택해 주세요.');
      return;
    }

    const body: CreateDealBody = {
      customerId: form.customer.id,
      storeId: form.store?.id,
      ownerEntity: form.ownerEntity,
      salesRepCode: form.salesRepCode,
      expectedRevenue: form.expectedRevenue || undefined,
      expectedRobotCount: form.expectedRobotCount ? Number(form.expectedRobotCount) : undefined,
      // §D-2 #5 (PDF p.13) — 예상 마감일 input 제거됨. expectedCloseAt
      // 컬럼 자체는 자동 분류기 입력값으로 보존되지만, UI 에서는 입력 안 함.
      expectedUnitPrice: form.expectedUnitPrice || undefined,
      expectedContractType: form.expectedContractType || undefined,
      expectedContractDurationMonths: form.expectedContractDurationMonths
        ? Number(form.expectedContractDurationMonths)
        : undefined,
    };
    create.mutate(body);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      // PDF p.13: 운영 용어로 "Deal" → "고객사" 통일. 내부 도메인 모델명 (Deal)
      // 은 코드/DB 그대로 유지 — UI 표기만 변경.
      title="신규 고객사"
      width={520}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="deal-create-form" disabled={create.isPending}>
            {create.isPending ? '생성 중…' : '고객사 생성'}
          </Button>
        </>
      }
    >
      <form id="deal-create-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <Field label="고객사" required>
          <CustomerPicker
            selected={form.customer?.id ?? null}
            onSelect={(c) =>
              setForm((f) => ({
                ...f,
                customer: c,
                // Customer's ownerEntity wins by default — server
                // enforces coherence anyway. Admins can flip below.
                ownerEntity: c.ownerEntity,
                // Reset store when customer changes (the StorePicker's
                // customer-scoped query would otherwise show stale results).
                store: null,
              }))
            }
            ownerEntity={user?.role === UserRole.ADMIN ? undefined : user?.ownerEntity}
          />
        </Field>

        <Field label="매장 (선택)">
          <StorePicker
            customerId={form.customer?.id ?? null}
            selected={form.store?.id ?? null}
            onSelect={(s) => setForm((f) => ({ ...f, store: s }))}
            allowNone
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="영업 코드" htmlFor="d-sales" required>
            <Select
              id="d-sales"
              value={form.salesRepCode}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  salesRepCode: e.target.value as SalesRepCode,
                }))
              }
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              {SALES_REP_CODES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="소유 법인" htmlFor="d-entity" required>
            <Select
              id="d-entity"
              value={form.ownerEntity}
              disabled={user?.role !== UserRole.ADMIN}
              onChange={(e) =>
                setForm((f) => ({ ...f, ownerEntity: e.target.value as OwnerEntity }))
              }
            >
              <option value={OwnerEntity.BEYOND_HONEYCOMB}>비욘드허니컴</option>
              <option value={OwnerEntity.HONEYCOMB_ROBOTICS}>허니컴로보틱스</option>
            </Select>
          </Field>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showPriceInputs ? '2fr 1fr' : '1fr',
            gap: 12,
          }}
        >
          {/* §D-3 — price input visible to sales / admin only.
              Non-sales users skip the field entirely; their submit
              still hits the server, which rejects the field via the
              price-mask interceptor + DTO validation. */}
          {showPriceInputs && (
            <Field
              label="예상 계약 단가 (대당, KRW)"
              htmlFor="d-unit-price"
              // 계약 사실화 1단계 정정7(26-07-29) — 고른 계약 형태에 따라 이 금액의
              // 뜻이 갈리므로 안내문도 함께 갈린다(위 unitPriceMeaningHint 주석).
              hint={unitPriceMeaningHint(form.expectedContractType)}
            >
              <Input
                id="d-unit-price"
                type="text"
                inputMode="numeric"
                value={form.expectedUnitPrice}
                placeholder="10000000"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    expectedUnitPrice: e.target.value.replace(/[^0-9]/g, ''),
                  }))
                }
                style={{ fontFamily: 'var(--font-mono)' }}
              />
            </Field>
          )}
          <Field label="로봇 대수" htmlFor="d-robots">
            <Input
              id="d-robots"
              type="number"
              min={0}
              value={form.expectedRobotCount}
              onChange={(e) => setForm((f) => ({ ...f, expectedRobotCount: e.target.value }))}
            />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/*
            Q#9 — 계약 형태 + 계약 기간. 옵션 매니저(API + admin UI)는
            Phase 3 후속 페이즈에서 wired up. 현재는 시드된 값 (RENTAL/SALE,
            24/36개월) 을 인라인 select 로 직접 노출.
          */}
          <Field label="계약 형태" htmlFor="d-contract-type">
            <Select
              id="d-contract-type"
              value={form.expectedContractType}
              onChange={(e) => setForm((f) => ({ ...f, expectedContractType: e.target.value }))}
            >
              <option value="">선택 안함</option>
              <option value="RENTAL">렌탈</option>
              <option value="SALE">판매</option>
            </Select>
          </Field>
          <Field label="계약 기간" htmlFor="d-contract-duration">
            <Select
              id="d-contract-duration"
              value={form.expectedContractDurationMonths}
              onChange={(e) =>
                setForm((f) => ({ ...f, expectedContractDurationMonths: e.target.value }))
              }
            >
              <option value="">선택 안함</option>
              <option value="24">2년</option>
              <option value="36">3년</option>
            </Select>
          </Field>
        </div>

        {showPriceInputs && (
          <Field
            label="예상 매출 (선택)"
            htmlFor="d-revenue"
            hint="대당 단가와 수량으로 자동 계산됩니다. 직접 입력해도 됩니다."
          >
            <Input
              id="d-revenue"
              type="text"
              inputMode="numeric"
              value={form.expectedRevenue}
              placeholder="비워 두면 자동 계산"
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  expectedRevenue: e.target.value.replace(/[^0-9]/g, ''),
                }))
              }
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        )}
      </form>
    </Modal>
  );
}
